/**
 * ReviewEditDetailsStep
 *
 * Step 2 of the review edit wizard: written review, recommendation,
 * highlights, and areas for improvement.
 * Used inside CustomerReviewEdit's progress Card.
 */
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import Button from '../common/Button';

export default function ReviewEditDetailsStep({ reviewData, onChangeField, canProceed, onBack, onNext }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Share Your Experience</h3>
        <p className="text-sm text-gray-600 mb-6">
          Tell other adventurers about your trip
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Written Review * (minimum 50 characters)
        </label>
        <textarea
          value={reviewData.review_text}
          onChange={(e) => onChangeField('review_text', e.target.value)}
          placeholder="Describe your experience, what you enjoyed, and what could be improved..."
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-xs text-gray-500 mt-1">
          {reviewData.review_text.length} / 50 characters
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Would you recommend this experience? *
        </label>
        <div className="flex space-x-4">
          <button
            onClick={() => onChangeField('would_recommend', true)}
            className={`flex-1 px-4 py-3 rounded-md border-2 transition-colors ${
              reviewData.would_recommend === true
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
            }`}
          >
            <ThumbsUp className="w-6 h-6 mx-auto mb-1" />
            <span className="block text-sm font-medium">Yes, Recommend</span>
          </button>
          <button
            onClick={() => onChangeField('would_recommend', false)}
            className={`flex-1 px-4 py-3 rounded-md border-2 transition-colors ${
              reviewData.would_recommend === false
                ? 'border-red-500 bg-red-50 text-red-700'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
            }`}
          >
            <ThumbsDown className="w-6 h-6 mx-auto mb-1" />
            <span className="block text-sm font-medium">No, Don&apos;t Recommend</span>
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Trip Highlights (Optional)
        </label>
        <textarea
          value={reviewData.trip_highlights}
          onChange={(e) => onChangeField('trip_highlights', e.target.value)}
          placeholder="What were the best moments of your trip?"
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Areas for Improvement (Optional)
        </label>
        <textarea
          value={reviewData.areas_improvement}
          onChange={(e) => onChangeField('areas_improvement', e.target.value)}
          placeholder="What could have been better?"
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="secondary" onClick={onBack}>Back</Button>
        <Button onClick={onNext} disabled={!canProceed}>Preview Changes</Button>
      </div>
    </div>
  );
}
